import type { VimeoUrl } from '@vimeo/player';

/** The type of video object in Vimeo API. For now, it is a subset of all valid
 *  fields; for the full reference of available data see:
 *  https://developer.vimeo.com/api/reference/response/video. */
export type VimeoVideoT = {
  /** Video duration in seconds. */
  duration: number;

  files_size: {
    // TODO: In fact, it is an enum, see:
    // https://developer.vimeo.com/api/reference/response/video-versions-size
    // but we don't use at the moment, so not defined in the code.
    file_size_type: string;
    num_versions: number;
    total_size: number;
  };

  has_audio: boolean;

  height: number;

  /** Switches to "true" once the uploaded video has been processed and is ready
   *  to be played; while it is "false" the video URL (`link` field) won't work. */
  is_playable: boolean;

  /** Video URL. */
  link: string;

  /** File name. */
  name: string;

  pictures: {
    active: boolean;
    base_link: string;
    sizes: Array<{
      height: number;
      link: string;
      width: number;
    }>;
  };

  status: string;

  upload: {
    status: string;
    upload_link: string;
  };

  /** Video URI, e.g. "/videos/12345" */
  uri: string;

  width: number;
};

/**
 * A subset of Vimeo video data we are confident there is no need to keep
 * secret.
 */
export type PublicVimeoVideoDataT = {
  name: string;
  size: number;
};

/** Vimeo user record. Currently a subset of available data; for the full
 *  reference see: https://developer.vimeo.com/api/reference/response/user.
 */
export type VimeoUserT = {
  /** User URI, in the form "/users/{user_id}" */
  uri: string;
};

// TODO: Hmm... perhaps this is wrong for videos inside teams? or folders?
// see, for example: https://vimeo.com/1094662036/e8701f6fac

/** Extracts Vimeo video ID from its URL. */
function getVimeoVideoId(url: string, dontThrow?: false): string;
function getVimeoVideoId(url: string, dontThrow: true): null | string;

function getVimeoVideoId(url: string, dontThrow?: boolean): null | string {
  const id = url.match(
    /^https:\/\/(?:player.vimeo.com\/video|vimeo.com)\/(\d*)/,
  )?.[1];

  if (!id && !dontThrow) throw Error(`Not a valid Vimeo URL: "${url}"`);

  return id ?? null;
}

export { getVimeoVideoId };

export function isVimeoUrl(
  url: string | undefined,
): url is VimeoUrl | undefined {
  const PREFIX_A: VimeoUrl = 'https://vimeo.com/';
  const PREFIX_B: VimeoUrl = 'https://player.vimeo.com/video/';
  return url === undefined
    || url.startsWith(PREFIX_A)
    || url.startsWith(PREFIX_B);
}

type VideoUrlKindT = 'VIMEO';

// TODO: Not sure, whether we need both this, and isVimeoUrl() above...
// they were originally created for difference pieces of code, then moved
// to his place... need to revisit how they are used, and only leave one of
// them, I guess.
export function deduceVideoUrlKind(url: string): VideoUrlKindT | null {
  return url.match(/^https:\/\/vimeo.com\//) && 'VIMEO';
}
